// @owners { team: patients-team }
import { Description, InlineAlert, MdSpacing } from '@alto/design-system';
import { type FertilityCycleMedicationType } from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';
import { format, parse } from 'date-fns';
import React from 'react';
import { CONTACT_US_ORIGIN } from '~shared/constants';
import { DATE_FORMATS } from '~shared/helpers/date';
import {
  type MedicationBlockers,
  QUANTITY_REVIEW_STATUS,
  type QuantityReviewStatus,
} from '~shared/selectors/progyny-wizard/getQuantityReviewStatus';
import { ContactUs } from '../../alto-assistant';
import { PhoneNumber } from '../../phone-number';
import {
  BLOCKER_SUPPLY_LIMITS,
  FULL_MONTH_NO_YEAR_DATE_FORMAT,
  MEDICATION_TYPES_DISPLAY,
  QUANTITIES_REVIEW_STRINGS,
} from '../constants';
type Props = {
  readonly quantityReviewStatus: QuantityReviewStatus;
  readonly blockerMeds: MedicationBlockers;
  readonly triggerDate: string | null | undefined;
};

function formatMedications(medications: FertilityCycleMedicationType[]) {
  return medications
    .map((med) => MEDICATION_TYPES_DISPLAY[med])
    .filter(Boolean)
    .join(' and ');
}

function blockerExceedSupplyMessage(blockerMeds: MedicationBlockers) {
  const cetrotideValue: FertilityCycleMedicationType = 'cetrotide';

  const medications = Object.values(blockerMeds);
  const notCetrotide = medications.filter((med) => med !== cetrotideValue);
  const includesCetrotide = medications.length !== notCetrotide.length;

  const nonCetrotideMessage = QUANTITIES_REVIEW_STRINGS.BLOCKER_EXCEED_SUPPLY(
    formatMedications(notCetrotide),
    BLOCKER_SUPPLY_LIMITS.default,
  );
  const cetrotideMessage = QUANTITIES_REVIEW_STRINGS.BLOCKER_EXCEED_SUPPLY(
    formatMedications([cetrotideValue]),
    BLOCKER_SUPPLY_LIMITS.cetrotide,
  );

  switch (medications.length) {
    case 0:
      return '';
    case 1:
      return includesCetrotide ? cetrotideMessage : nonCetrotideMessage;
    default:
      return includesCetrotide ? `${nonCetrotideMessage} ${cetrotideMessage}` : nonCetrotideMessage;
  }
}

export default function QuantitiesReviewAlert({ quantityReviewStatus, blockerMeds, triggerDate }: Props) {
  if (quantityReviewStatus === QUANTITY_REVIEW_STATUS.blocker_exceeded_allowed_days_supply) {
    return (
      <InlineAlert type="warning">
        <Description fontFamily="semibold">{QUANTITIES_REVIEW_STRINGS.BLOCKER_HEADER}</Description>
        <Description>
          {blockerExceedSupplyMessage(blockerMeds)} Please{' '}
          <ContactUs
            type="link"
            origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert}
            method="message"
            textSize="mini"
          />
          , or if urgent, call us at <PhoneNumber origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert} /> to assist you in
          scheduling your refills.
        </Description>
      </InlineAlert>
    );
  }

  if (quantityReviewStatus === QUANTITY_REVIEW_STATUS.blocker_trigger_date) {
    return (
      <InlineAlert type="warning">
        <Description>
          {QUANTITIES_REVIEW_STRINGS.BLOCKER_TRIGGER} Please{' '}
          <ContactUs
            type="link"
            origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert}
            method="message"
            textSize="mini"
          />
          , or if urgent, call us at <PhoneNumber origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert} /> to assist you.
        </Description>
      </InlineAlert>
    );
  }

  if (quantityReviewStatus === QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase) {
    return (
      <InlineAlert>
        <Description fontFamily="semibold">{QUANTITIES_REVIEW_STRINGS.INFO_HEADER}</Description>
        <Description>{QUANTITIES_REVIEW_STRINGS.INFO_WEEKEND_QUANTITY_INCREASE}</Description>
      </InlineAlert>
    );
  }

  if (quantityReviewStatus === QUANTITY_REVIEW_STATUS.info_weekend_quantity_increase_trigger_date) {
    return (
      <InlineAlert>
        <Description fontFamily="semibold">{QUANTITIES_REVIEW_STRINGS.INFO_HEADER}</Description>
        <Description>{QUANTITIES_REVIEW_STRINGS.INFO_WEEKEND_QUANTITY_INCREASE}</Description>
        <MdSpacing />
        <Description>
          {QUANTITIES_REVIEW_STRINGS.INFO_WEEKEND_TRIGGER(
            triggerDate
              ? format(parse(triggerDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0), FULL_MONTH_NO_YEAR_DATE_FORMAT)
              : '',
          )}
          please{' '}
          <ContactUs
            type="link"
            origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert}
            method="message"
            textSize="mini"
          />
          , or if urgent, call us at <PhoneNumber origin={CONTACT_US_ORIGIN.QuantitiesReviewAlert} />.
        </Description>
      </InlineAlert>
    );
  }

  return null;
}
